import { Layers, LineChart, Scale, ShieldCheck, Target } from 'lucide-react';
import * as React from 'react';

import { Badge } from '../../components-ui/Badge';
import { Button } from '../../components-ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components-ui/Dialog';
import { LogoIcon } from '../../components/Logo';
import { cn } from '../../lib/utils';
import useOrganizationStore from '../../store/organization';
import type { Feature } from '../features';
import { CURRENT_PLAN_NAME, INTEREST_CTA_LABEL, neutralUpsellCopyFor, upsellCopyFor } from './copy';
import { NEXOYA_DEMO_URL } from './config';
import { buildInterestMailto, openMailto } from './interestMailto';
import { planAdditions, type PlanAdditionId } from './planAdditions';
import { ScenarioPreview } from './ScenarioPreview';
import { scenarioFor } from './scenarios';

/**
 * `--ring: none` (global.css:37) makes `Button`'s `focus-visible:ring-ring` resolve to nothing, so
 * every focusable here names its ring. Ink, not the house green of `src/lib/focusRing.ts`: a ring
 * must clear 3:1 (WCAG 2.4.11), green-400 measures 2.23:1 on white, neutral-900 18.21:1 anywhere.
 */
export const FOCUS_RING = 'focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2';

/**
 * Radix's close button, restyled from this call site so the shared primitive is left alone.
 * Sizes it to 24x24 (WCAG 2.5.8) and gives it a ring, since `--ring` is dead (`global.css:37`).
 * Every pseudo-class must sit INSIDE the brackets — `[&>x]:hover:` targets the class, not `&`.
 * Neither pane may carry `z-index`, `transform` or `filter`: a stacking context swallows clicks.
 */
export const CLOSE_BUTTON_CHROME = [
  '[&>button:last-of-type]:inline-flex',
  '[&>button:last-of-type]:h-6',
  '[&>button:last-of-type]:w-6',
  '[&>button:last-of-type]:items-center',
  '[&>button:last-of-type]:justify-center',
  '[&>button:last-of-type]:rounded-md',
  '[&>button:last-of-type]:opacity-100',
  '[&>button:last-of-type]:text-neutral-500',
  '[&>button:last-of-type:hover]:text-neutral-900',
  '[&>button:last-of-type:focus-visible]:ring-2',
  '[&>button:last-of-type:focus-visible]:ring-neutral-900',
  '[&>button:last-of-type:focus-visible]:ring-offset-0',
].join(' ');

/**
 * The scroll region's own focus ring.
 *
 * It is a tab stop (see the layout notes below), so it needs an indicator like anything else —
 * and it is also what receives OPENING focus, so `focus:outline-none` here meant a dialog that
 * appeared with no visible focus anywhere on it. `ring-inset` rather than `FOCUS_RING`'s
 * offset: an offset ring on a scroll container paints outside the container's own clip and is
 * cut off on three sides.
 */
const SCROLLER_FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-neutral-900';

/** The green-tinted brand shadow. No Tailwind token, so it is written out. */
const CTA_SHADOW = 'shadow-[0px_2px_3px_0px_rgba(7,161,83,0.24)]';

/**
 * The shared opening clause of the body paragraph.
 *
 * A factual statement of what this plan covers, rendered in front of `copy.ts`'s per-feature
 * description — so it has to stay true against `FEATURES`, where `performance.view` and
 * `budget.view` are the two keys the Starter plan holds. Vendor- and plan-name-neutral, so the
 * same sentence is honest on a white-label tenant.
 */
const AGENCY_SCOPE_SENTENCE = 'Your plan covers the portfolio Performance and Budget tabs.';

/** Icons are presentational; the row title carries the meaning. Exhaustive by `PlanAdditionId`. */
type AdditionIcon = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
const ADDITION_ICONS: Record<PlanAdditionId, AdditionIcon> = {
  channels: Layers,
  attribution: Target,
  simulations: LineChart,
  rebalancing: Scale,
  validation: ShieldCheck,
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: Feature;
};

/**
 * The upsell moment, as two panes: the pitch on the left, what the premium plan adds on the
 * right.
 *
 * WHAT THE DIALOG IS NAMED. `DialogTitle` is the capability from `copy.ts` — never the product
 * and never a generic hero line. It is the only thread tying the control the
 * analyst clicked to the screen they landed on, and it is what a screen reader announces first.
 * What this plan covers is stated by `AGENCY_SCOPE_SENTENCE`, in front of the per-feature
 * description: body copy rather than the name, and true against `FEATURES`.
 *
 * SCROLLING, AND WHY THERE IS EXACTLY ONE SCROLLER. `DialogContent` is `flex` rather than the
 * primitive's `grid`, and its single child carries `min-h-0 flex-1 overflow-y-auto`: a grid
 * item's `min-height: auto` refuses to shrink, so `max-h` alone CLIPS instead of scrolling and
 * the CTA becomes unreachable at 200% zoom (WCAG 1.4.10). Two panes make that harder, not
 * easier, so the scroller wraps BOTH of them rather than each pane owning its own. Two
 * independent scrollers would mean the CTA sits at the bottom of the right pane's overflow —
 * fine with a mouse, invisible to anyone who does not think to scroll a region they cannot see
 * the edge of, and impossible to tab to past the left pane's content. One scroller also means
 * the stacked layout below `md:` needs no separate reasoning: it is the same element.
 *
 * The scroller is NOT the two-pane row — it wraps it. A flex item stretches to the flex line's
 * cross size, which inside a scroll container is the visible height and not the scroll height,
 * so a scroller doing double duty as the row left both panes ending at the fold with the rest
 * of the content painted on Content's own background. It is also a real tab stop (`tabIndex`
 * 0, with its own ring): the left pane holds no focusable element at all, so at `-1` its lower
 * half was unreachable by keyboard once the card overflowed.
 *
 * The scroller stays a plain descendant of Content, so it is inside both the focus trap and
 * `RemoveScroll`. The close button is NOT inside it (it is Content's last child), so it stays
 * pinned in the right pane's corner while the body scrolls.
 *
 * LAYOUT. Below `md:` the panes stack with the LEFT PANE FIRST, in DOM order and on screen, so
 * reading order and tab order never diverge across the breakpoint. Both panes carry `min-w-0`:
 * a flex item's `min-width: auto` refuses to shrink below its content's intrinsic minimum, and
 * one long unbroken string would re-create the horizontal overflow Content's `max-w` exists to
 * stop.
 *
 * HONESTY. Nothing here is invented. The rail comes from `planAdditions()`, which derives
 * locked-ness from `FEATURES`; the preview card's figures are illustrative and say so on their
 * own chip. An earlier draft framed a `metric` no query produces — a fabricated figure costs
 * more credibility than it earns, and an analyst at a large agency will spot it. The CTA
 * promises only what it does: it opens an email. The product sends nothing.
 *
 * WHITE-LABEL. On a tenant that resolves to `whiteLabel` there is no logomark, no wordmark, no
 * vendor plan name on the badge, no green revenue fill (the preview card is `muted`), the CTA
 * is a secondary mailto to the tenant's own desk under the SAME vendor-free label the Nexoya
 * anchor carries — only the affordance forks — and the per-feature copy comes
 * from `neutralUpsellCopyFor`, not `upsellCopyFor`. That last one is the part that was missed
 * for a release: four of `copy.ts`'s titles name the vendor, and the title is both this
 * dialog's accessible name and the `capability` in the outbound mail, so "Connect every
 * channel Nexoya supports." was reaching an agency's own client and their own support inbox.
 * Same gate as `src/Auth/Unauthorized.tsx`; the test sweeps every `Feature` rather than
 * sampling one, because sampling is how it went unnoticed.
 *
 * UNKNOWN. `tenantBrand === null` is a third state, not a synonym for white-label: everything
 * that names or implies a vendor stays off, and the CTA is the VENDOR-FREE one — never nothing.
 * Vendor-free is safe on every tenant by construction, so it is the correct default for "we do
 * not know yet"; `interestMailto.ts` drops an unusable address to an empty `To:` rather than to
 * ours, so the unknown state cannot leak an address either. Hiding a control is only right when
 * every version of it would leak, and rendering none here left a session whose tenant never
 * resolves with a locked panel and no way out of it at all.
 *
 * Hierarchy is carried by size and tightness, not weight: EuclidCircularB ships only 400 and
 * 500, so `font-semibold` anywhere here would be synthesised faux-bold.
 */
export function UpsellDialog({ open, onOpenChange, feature }: Props) {
  // Three states (`./tenant.ts`), and `null` is not a tenant: it takes every vendor-free branch
  // below — copy, chrome and CTA — because vendor-free is true whichever way it resolves.
  const isNexoyaTenant = useOrganizationStore((s) => s.tenantBrand) === 'nexoya';
  const supportMail = useOrganizationStore((s) => s.customization?.supportMail);

  // The per-feature copy, in the register this tenant may read. Four of `copy.ts`'s titles
  // name the vendor, and this one string is rendered as the dialog's accessible name AND
  // passed through as the mail's `capability` — so on a white-label tenant the branded version
  // reached both the client's screen and the agency's own support inbox.
  const { title, description } = isNexoyaTenant ? upsellCopyFor(feature) : neutralUpsellCopyFor(feature);

  const scenario = scenarioFor(feature);
  const additions = planAdditions();

  const bodyRef = React.useRef<HTMLDivElement>(null);

  const mailtoHref = buildInterestMailto({ to: supportMail, capability: title, isNexoyaTenant });

  // The CTA stays mounted and keeps focus: the mail client opens out of band, so there is
  // nothing for this app to confirm and no control swap to announce.
  const handleInterest = () => openMailto(mailtoHref);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Radix's `FocusScope` autofocuses the first tabbable non-link, which is now the sales
        // CTA — a dialog that opens with a buy button under the keyboard. Focus goes to the
        // scroll region instead, which is a real tab stop with a ring of its own so the landing
        // place is both visible and returnable-to.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          bodyRef.current?.focus();
        }}
        className={cn(
          'flex max-h-[85vh] w-full max-w-[1180px] flex-col gap-0 overflow-hidden rounded-xl border-0 p-0 shadow-[0px_16px_24px_0px_rgba(19,19,20,0.24)] sm:rounded-xl',
          CLOSE_BUTTON_CHROME,
        )}
      >
        <div ref={bodyRef} tabIndex={0} className={cn('min-h-0 flex-1 overflow-y-auto', SCROLLER_FOCUS_RING)}>
          {/*
           * The two-pane row is INSIDE the scroller, never the scroller itself.
           *
           * A flex item stretches to the flex line's cross size, and in a scroll container that
           * is the visible height rather than the scroll height. With the scroller doing double
           * duty as the row, both panes stopped at the fold: measured at 1194x800, the content
           * ran to 761px while each pane was 680px, so the footnote and "Not now" rendered on
           * `DialogContent`'s white background past the end of the grey pane and its divider.
           *
           * `min-h-full` is the other half: the row sizes to its own content when the content
           * overflows, and fills the scroller when it does not, so a short dialog does not show
           * white under the grey pane either. Still exactly one scroller.
           */}
          <div className="flex min-h-full flex-col md:flex-row">
            {/* ───────── LEFT PANE: the pitch ───────── */}
            <div className="w-full min-w-0 bg-white px-8 py-8 pr-14 md:w-[52%] md:px-10 md:py-[34px] md:pr-10">
              {/* Extra right padding below `md:` keeps the brand row clear of the close button,
                which is `absolute right-4 top-4` on Content and overlaps the left pane once
                the panes stack. */}
              <div className="flex items-center gap-2.5">
                {isNexoyaTenant ? (
                  <>
                    <span data-testid="upsell-logomark" className="inline-flex">
                      <LogoIcon animationDisabled className="h-5 w-[18px] shrink-0" />
                    </span>
                    <span className="text-[13px] uppercase tracking-[1.6px] text-neutral-900">Nexoya</span>
                  </>
                ) : null}
                <Badge variant="full" className="bg-green-100 text-green-800">
                  {isNexoyaTenant ? CURRENT_PLAN_NAME : 'Current plan'}
                </Badge>
              </div>

              <DialogTitle className="mt-6 max-w-[485px] text-[26px] font-normal leading-[1.15] tracking-[-0.4px] text-neutral-900 md:text-[28px]">
                {title}
              </DialogTitle>

              <DialogDescription className="mt-4 max-w-[485px] text-[13px] leading-[1.55] text-neutral-500">
                {AGENCY_SCOPE_SENTENCE} {description}
              </DialogDescription>

              <p className="mt-7 text-[11px] uppercase tracking-[0.6px] text-neutral-500">{scenario.eyebrow}</p>

              <ScenarioPreview scenario={scenario} muted={!isNexoyaTenant} className="mt-3" />
            </div>

            {/* ───────── RIGHT PANE: what the premium plan adds ───────── */}
            <div className="flex w-full min-w-0 flex-col border-t border-neutral-100 bg-neutral-50 px-7 pb-7 pt-12 md:w-[48%] md:border-l md:border-t-0 md:px-[30px] md:pb-7 md:pt-12">
              <h2 id="upsell-additions-heading" className="text-[14px] font-medium text-neutral-900">
                What the premium plan adds
              </h2>

              <ul
                data-testid="upsell-additions"
                aria-labelledby="upsell-additions-heading"
                className="mt-5 flex flex-col gap-[18px]"
              >
                {additions.map((row) => {
                  const Icon = ADDITION_ICONS[row.id];
                  const isCurrent = row.feature !== null && row.feature === feature;
                  return (
                    /* `aria-current` on the <li>: it has an implicit `listitem` role, and NVDA
                     and JAWS do not reliably announce `aria-current` on a role-less generic. */
                    <li key={row.id} aria-current={isCurrent ? 'true' : undefined} className="flex gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-neutral-100 bg-white">
                        {/* 2.23:1 on white, and deliberately not "fixed": the icon is decoration
                          whose meaning is already in the title beside it, so 1.4.11 does not
                          apply. Keep it `aria-hidden`. */}
                        <Icon className="h-3.5 w-3.5 text-green-400" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        {/* `leading-7` matches the tile's `h-7`: equal line boxes share a centre.
                          Inherited 1.5 gave 19.5px against a 28px tile, so icons sat 4.25px low. */}
                        <span
                          data-testid="upsell-addition-title"
                          className="block text-[13px] font-medium leading-7 text-neutral-900"
                        >
                          {row.title}
                          {isCurrent ? <span className="sr-only"> — the capability you selected</span> : null}
                        </span>
                        {/* Integer line box: 1.45 x 12px = 17.4px put every row on its own sub-pixel
                          phase, so Chrome antialiased each icon differently. */}
                        <span className="block text-[12px] leading-[18px] text-neutral-500">{row.description}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>

              {/* Pinned to the foot of the pane: `mt-auto` eats the slack when the rail is short
                  and collapses when it is not, so `pt-6` is what guarantees the gap. Still INSIDE
                  the one scroller — a footer outside it is the second scroll region this layout
                  exists to avoid. */}
              <div className="mt-auto pt-6">
                {isNexoyaTenant ? (
                  /* An anchor, not a button: it navigates to nexoya.com/demo, and a real link
                     is what gives the analyst middle-click, copy-link and their own new tab. */
                  <Button
                    asChild
                    variant="success"
                    className={cn('h-11 w-full text-[14px] font-medium', CTA_SHADOW, FOCUS_RING)}
                  >
                    <a href={NEXOYA_DEMO_URL} target="_blank" rel="noopener noreferrer">
                      {INTEREST_CTA_LABEL}
                    </a>
                  </Button>
                ) : (
                  /* White-label AND unknown: a button, because it opens a mailto rather than
                     navigating. Same primary fill as the Nexoya arm — one intent, one weight. */
                  <Button
                    type="button"
                    variant="success"
                    onClick={handleInterest}
                    className={cn('h-11 w-full text-[14px] font-medium', CTA_SHADOW, FOCUS_RING)}
                  >
                    {INTEREST_CTA_LABEL}
                  </Button>
                )}

                <Button
                  type="button"
                  variant="link"
                  onClick={() => onOpenChange(false)}
                  className={cn('mt-2 h-auto w-full text-[12px] text-neutral-500', FOCUS_RING)}
                >
                  Not now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UpsellDialog;
